import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';

/* ===== Helper: 뷰포트별 첫 줄 아이템 개수 계산 ===== */
function useFirstRowCount() {
  const getCount = () => {
    if (typeof window === 'undefined') return 4;
    const w = window.innerWidth;
    if (w <= 480) return 1;      // 작은 모바일: 1열
    if (w <= 768) return 2;      // 모바일: 2열
    if (w <= 1024) return 3;     // 태블릿: 3열
    return 4;                    // 데스크톱: 4열
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const onResize = () => setCount(getCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return count;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;

  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    white-space: pre-line;
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => (props.active ? '#3b82f6' : '#6b7280')};
  background: none;
  border: none;
  border-bottom: 2px solid ${props => (props.active ? '#3b82f6' : 'transparent')};
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 1rem;

  &:hover {
    color: #3b82f6;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    margin: 0 0.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 4열 */
  gap: 3rem;
  margin-bottom: 4rem;
  max-width: 1400px;
  margin: 0 auto 4rem auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr); /* 태블릿 3열 */
    gap: 2.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr); /* 모바일 2열 */
    gap: 1.7rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr; /* 작은 모바일 1열 */
    gap: 1.5rem;
  }
`;

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }
`;

/* (2) 오프스크린 렌더 지연 + 자리 선점으로 CLS 방지 */
const ContentImage = styled.div`
  content-visibility: auto;
  contain-intrinsic-size: 400px;   /* 기본 높이만큼 자리 확보 */
  width: 100%;
  height: 400px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 450px;
    contain-intrinsic-size: 450px;
  }
`;

const ContentImagePlaceholder = styled.div`
  color: #94a3b8;
  font-size: 1rem;
  text-align: center;
  padding: 1rem;
  line-height: 1.4;
`;

const ContentInfo = styled.div`
  padding: 1.5rem 1rem;
  text-align: center;
  background: #ffffff;
`;

const ContentTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111111;
  margin: 0;
  line-height: 1.4;
  word-break: keep-all;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

/* ===== 데이터 ===== */
const tabData = {
  certifications: [
    { title: '사업자등록증', image: '/images/certifications/certifis/certifies1.webp' },
    { title: '공장등록증명서', image: '/images/certifications/certifis/certifies2.webp' },
    { title: '벤처기업 확인서', image: '/images/certifications/certifis/certifies3.webp' },
    { title: '중소기업 확인서', image: '/images/certifications/certifis/certifies4.webp' },
    { title: '기업부설연구소 인증서', image: '/images/certifications/certifis/certifies5.webp' },
    { title: '사회적기업 인증서', image: '/images/certifications/certifis/certifies6.webp' },
    { title: '프리(pre)-명품강소기업 지정서', image: '/images/certifications/certifis/certifies7.webp' },
    { title: '조달청 벤처나라 지정증서\n디딤-safe', image: '/images/certifications/certifis/certifies8.webp' },
    { title: '조달청 벤처나라 지정증서\n캐리어형 비상표시장치', image: '/images/certifications/certifis/certifies9.webp' },
    { title: '재난안전제품인증서', image: '/images/certifications/certifis/certifies10.webp' },
    { title: '중소기업기술마켓 인증서\n디딤-safe', image: '/images/certifications/certifis/certifies11.webp' },
    { title: '국토교통부 혁신제품 추천서\n디딤-safe', image: '/images/certifications/certifis/certifies12.webp' },
    { title: '경영혁신형 중소기업 확인서', image: '/images/certifications/certifis/certifies13.webp' },
    { title: '기술혁신형 중소기업 확인서', image: '/images/certifications/certifis/certifies14.webp' },
    { title: '제품인증서\n반사 안전 표지판', image: '/images/certifications/certifis/certifies15.webp' },
    { title: 'G-CERTI certificate', image: '/images/certifications/certifis/certifies16.webp' },
    { title: '글로벌시스템인증', image: '/images/certifications/certifis/certifies17.webp' },
    { title: '직접생산확인증명서', image: '/images/certifications/certifis/certifies18.webp' },
    { title: '직무발명보상우수기업 인증서', image: '/images/certifications/certifis/certifies19.webp' },
    { title: '지식재산경영인증서', image: '/images/certifications/certifis/certifies20.webp' },
    { title: '위험성평가 인정서', image: '/images/certifications/certifis/certifies21.webp' },
  ],
  patents: [
    { title: '특허증\n도로 가드레일용 발판구조물', image: '/images/certifications/patents/patents1.webp' },
    { title: '특허증\n이동식 비상표시장치', image: '/images/certifications/patents/patents2.webp' },
    { title: '특허증\n불꽃신호기 점화장치', image: '/images/certifications/patents/patents3.webp' },
    { title: '디자인등록증\n안전 경고등', image: '/images/certifications/patents/patents4.webp' },
    { title: '디자인등록증\n도로용 가드레일 발판 구조체', image: '/images/certifications/patents/patents5.webp' },
    { title: '디자인등록증\n도로용 가드레일 발판', image: '/images/certifications/patents/patents6.webp' },
    { title: '특허증\n베너 걸이장치', image: '/images/certifications/patents/patents7.webp' },
    { title: '특허증\n차량 탑재형 비상표시장치', image: '/images/certifications/patents/patents8.webp' },
    { title: '특허증\n시선 유도등 시스템', image: '/images/certifications/patents/patents9.webp' },
    { title: '특허증\n휴대용 비상표시장치', image: '/images/certifications/patents/patents10.webp' },
    { title: '특허증\n캐리어형 비상표시장치', image: '/images/certifications/patents/patents11.webp' },
    { title: '특허증\n휴대형 비상표시장치', image: '/images/certifications/patents/patents12.webp' },
    { title: '특허증\n휴대용 비상 알림 표시기', image: '/images/certifications/patents/patents13.webp' },
    { title: '특허증\n발광, 반사 겸용 표지판', image: '/images/certifications/patents/patents14.webp' },
    { title: '특허증\n암반 붕괴 감지시스템', image: '/images/certifications/patents/patents15.webp' },
    { title: '특허증\n교량 집수장치', image: '/images/certifications/patents/patents16.webp' },
    { title: '디자인등록증\n사면감지기 컨트롤박스', image: '/images/certifications/patents/patents17.webp' },
    { title: '특허증\n발광형 도로 표지판', image: '/images/certifications/patents/patents18.webp' },
    { title: '특허증\n발광형 제한속도 표시장치', image: '/images/certifications/patents/patents19.webp' },
    { title: '특허증\n비상표시장치', image: '/images/certifications/patents/patents20.webp' },
    { title: '특허증\n비탈면 붕괴 사전 감지시스템', image: '/images/certifications/patents/patents21.webp' },
    { title: '디자인등록증\n휀스 지주용 고정구', image: '/images/certifications/patents/patents22.webp' },
    { title: '디자인등록증\n제10류 사면감지기 컨트롤박스', image: '/images/certifications/patents/patents23.webp' },
    { title: '서비스표등록증\n제37류 사면 붕과 사전 감지기 설치 공사업등 1', image: '/images/certifications/patents/patents24.webp' },
    { title: '상표등록증\n올빼미', image: '/images/certifications/patents/patents25.webp' },
  ],
  awards: [
    { title: '제11회 2022대한민국모범기업인대상\n대상', image: '/images/certifications/awards/awards1.webp' },
    { title: '광주지방조달청장\n우수벤처기업인 표창', image: '/images/certifications/awards/awards2.webp' },
    { title: '제7회 2020대한민국지역사회공헌대상\n대상', image: '/images/certifications/awards/awards3.webp' },
    { title: 'IR52 장영실 기술혁신상\n기술혁신 우수기업 선정', image: '/images/certifications/awards/awards4.webp' },
    { title: '제53회 발명의 날\n국무총리상 표창', image: '/images/certifications/awards/awards5.webp' },
    { title: '제51회 과학의 날\n표창', image: '/images/certifications/awards/awards6.webp' },
    { title: '광주전남중소기업청 우수중소기업인 표창', image: '/images/certifications/awards/awards7.webp' },
    { title: '2016대한민국국민브랜드대상\n도로안전관리부문 대상', image: '/images/certifications/awards/awards8.webp' },
    { title: '2016대한민국우수상품대상\n대상', image: '/images/certifications/awards/awards9.webp' },
    { title: '제10회 2015대한민국나눔대상\n대상', image: '/images/certifications/awards/awards10.webp' },
  ],
};

const tabTitles = {
  certifications: '인증',
  patents: '특허',
  awards: '수상실적',
};

function Certifications() {
  const [activeTab, setActiveTab] = useState('certifications');
  const [headerRef, headerVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();
  const eagerCount = useFirstRowCount();

  const handleItemClick = (item) => {
    console.log('Item clicked:', item.title);
  };

  const currentData = tabData[activeTab] || [];

  return (
    <PageContainer>
      <Container>
        
        <PageHeader>
          <PageTitle>인증 및 수상실적</PageTitle>
          <PageSubtitle>
            {'세연이앤에스의 기술력과 품질을 인정받은 \n각종 인증서 및 특허와 수상실적을 소개합니다'}
          </PageSubtitle>
        </PageHeader>

        <TabContainer>
          {Object.keys(tabTitles).map((tabKey) => (
            <Tab key={tabKey} active={activeTab === tabKey} onClick={() => setActiveTab(tabKey)}>
              {tabTitles[tabKey]}
            </Tab>
          ))}
        </TabContainer>
        

        
        <ContentGrid>
          {currentData.map((item, index) => (
            <ContentCard key={index} onClick={() => handleItemClick(item)}>
              <ContentImage>
                {/* (1) 지연 로딩 + 우선순위 + 비동기 디코딩 */}
                <img
                  src={item.image}
                  alt={item.title}
                  loading={index < eagerCount ? 'eager' : 'lazy'}
                  fetchpriority={index < eagerCount ? 'high' : 'low'}
                  decoding="async"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
                <ContentImagePlaceholder style={{ display: 'none' }}>
                  {item.title}
                </ContentImagePlaceholder>
              </ContentImage>
              <ContentInfo>
                <ContentTitle>{item.title}</ContentTitle>
              </ContentInfo>
            </ContentCard>
          ))}
        </ContentGrid>
      </Container>
    </PageContainer>
  );
}

export default Certifications;
